import { useRef, useState } from "react";
import classNames from 'classnames';
import { debounce } from "underscore";
import { Validation } from "./text";
import Icon, { COLORS, ICONS } from "../icon";
import { useCallback } from "react";
import { useEffect } from "react";

interface IBasicInputProps {
	onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void;
	onDebounce?: (value: string) => void;
	debounceTime?: number;
	margin?: 'none' | 'normal';
	valid?: Validation;
	required?: boolean;
	label?: string;
	focus?: boolean;
	setFocus?: React.Dispatch<React.SetStateAction<boolean>>;
}

const BasicInput: React.FC<IBasicInputProps & React.HTMLProps<HTMLInputElement>> = ({
	onChange = () => null,
	onDebounce = () => null,
	debounceTime = 300,
	type = 'text',
	margin = 'normal',
	valid = Validation.normal,
	required = false,
	label,
	focus,
	setFocus,
	...rest
}) => {
	const [value, setValue] = useState('');
	const [showPassword, setShowPassword] = useState(false);

	const inputRef = useRef<HTMLInputElement | null>(null);
	const passwordTimer = useRef<NodeJS.Timeout | null>(null);

	const debounced = useRef(debounce((value: string) => {
		onDebounce(value);
	}, debounceTime)).current;

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
		debounced(e.target.value);
		onChange(e);
	};

	const togglePassword = useCallback((e: any) => {
		e.preventDefault();
		setShowPassword(!showPassword);
	}, [showPassword]);

	useEffect(() => {
		if (passwordTimer.current) clearTimeout(passwordTimer.current);

		if (showPassword) {
			passwordTimer.current = setTimeout(() => {
				setShowPassword(false);
			}, 3000);
		}
	}, [showPassword]);


	useEffect(() => {
		if (inputRef?.current && focus) {
			inputRef.current.focus();
		}
	}, [focus]);

	// if the parent passes down the method setFocus, then we want to make
	// sure we set it to false when the user blurs - note: we cannot use reacts onBlur method
	// because it will not trigger under every scenario
	useEffect(() => {
		const referencedRef = inputRef?.current;
		const handleBlur = () => {
			setFocus && setFocus(false);
		};
		if (referencedRef) {
			referencedRef.addEventListener('blur', handleBlur);
		}
		return () => {
			if (referencedRef) {
				referencedRef.removeEventListener('blur', handleBlur);
			}
		};
	}, [inputRef, setFocus]);

	return (
		<div className={`field-group text normal ${margin === 'none' ? 'no-margin' : ''} ${valid}`}>
			{label ? (
				<div
					style={{
						display: 'flex',
						alignItems: 'center'
					}}
				>
					<label
						className={classNames(
							"evt-field-label",
							{ "evt-field-label-required": required },
						)}
					>
						{label}
					</label>
				</div>
			) : null}
			<input
				ref={inputRef}
				type={showPassword ? 'text' : type}
				onChange={handleChange}
				value={value}
				{...rest}
			/>

			{type === 'password' && (
				<button onMouseDown={togglePassword} type="button" onTouchStart={togglePassword} className="no-style show-password"><Icon name={ICONS.VIEWERS_EYE} size={12} color={COLORS.WHITE} /></button>
			)}
		</div>
	);
};

export default BasicInput;
